/**
 * Store для управления тенантами (организациями)
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useApiStore } from './api'

export interface UserTenant {
  id: number
  user_id: number
  tenant_id: number
  tenant_name: string
  tenant_slug: string
  role: string
  is_default: boolean
  created_at: string
}

export const useTenantStore = defineStore('tenant', () => {
  const api = useApiStore()
  
  // State
  const currentTenant = ref<UserTenant | null>(null)
  const availableTenants = ref<UserTenant[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  
  // Computed
  const hasMultipleTenants = computed(() => availableTenants.value.length > 1)
  const currentTenantName = computed(() => currentTenant.value?.tenant_name || 'Без организации')
  const currentRole = computed(() => currentTenant.value?.role || 'user')
  const isAdmin = computed(() => ['tenant_admin', 'platform_admin'].includes(currentRole.value))
  
  // Actions
  
  /**
   * Загрузить список тенантов пользователя
   */
  async function loadUserTenants() {
    loading.value = true
    error.value = null
    
    try {
      const tenants = await api.getUserTenants()
      availableTenants.value = tenants
      
      // Извлекаем tenant_id из JWT токена
      let tokenTenantId: number | null = null
      try {
        const token = localStorage.getItem('token')
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]))
          tokenTenantId = payload.tenant_id || null
        }
      } catch (e) {
        console.warn('Could not extract tenant_id from token:', e)
      }
      
      // Устанавливаем текущий тенант:
      // 1. Приоритет - тенант из JWT токена
      // 2. Иначе - дефолтный тенант
      // 3. Иначе - первый доступный
      let selectedTenant = null
      if (tokenTenantId) {
        selectedTenant = tenants.find(t => t.tenant_id === tokenTenantId)
      }
      if (!selectedTenant) {
        selectedTenant = tenants.find(t => t.is_default)
      }
      currentTenant.value = selectedTenant || tenants[0] || null

      // 🧩 ВАЖНО: если tenant выбран в UI, но в JWT tenant_id=null (например platform_admin),
      // то Telegram endpoints с tenant-фильтрами будут видеть tenant_id=null и падать.
      // В этом случае автоматически получаем новый JWT через switch-tenant.
      if (!tokenTenantId && currentTenant.value?.tenant_id) {
        try {
          await switchTenant(currentTenant.value.tenant_id)
        } catch (e) {
          console.warn('[TenantStore] Auto switchTenant failed:', e)
        }
      }
      
      console.log('[TenantStore] Loaded tenants:', {
        total: tenants.length,
        token_tenant_id: tokenTenantId,
        selected: currentTenant.value?.tenant_name
      })
      
      return tenants
    } catch (err: any) {
      error.value = err.message || 'Ошибка загрузки списка организаций'
      console.error('Error loading tenants:', err)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Переключиться на другой тенант
   */
  async function switchTenant(tenantId: number) {
    loading.value = true
    error.value = null
    
    try {
      const result = await api.switchTenant(tenantId)
      
      // Обновляем токен в localStorage
      if (result.access_token) {
        localStorage.setItem('token', result.access_token)
      }
      
      // Обновляем текущий тенант
      const tenant = availableTenants.value.find(t => t.tenant_id === tenantId)
      if (tenant) {
        // Обновляем is_default локально
        availableTenants.value.forEach(t => {
          t.is_default = t.tenant_id === tenantId
        })
        currentTenant.value = tenant
      }
      
      return result
    } catch (err: any) {
      error.value = err.message || 'Ошибка переключения организации'
      console.error('Error switching tenant:', err)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Создать новый тенант
   */
  async function createTenant(data: { name: string; slug: string; notes?: string }) {
    loading.value = true
    error.value = null
    
    try {
      const tenant = await api.createOwnTenant(data)
      
      // Перезагружаем список тенантов
      await loadUserTenants()
      
      return tenant
    } catch (err: any) {
      error.value = err.message || 'Ошибка создания организации'
      console.error('Error creating tenant:', err)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Очистить store (при logout)
   */
  function clearStore() {
    currentTenant.value = null
    availableTenants.value = []
    error.value = null
  }
  
  /**
   * Инициализация store при загрузке приложения
   */
  async function initStore() {
    const token = localStorage.getItem('token')
    if (token) {
      try {
        await loadUserTenants()
      } catch (err) {
        console.error('Error initializing tenant store:', err)
      }
    }
  }
  
  return {
    // State
    currentTenant,
    availableTenants,
    loading,
    error,
    
    // Computed
    hasMultipleTenants,
    currentTenantName,
    currentRole,
    isAdmin,
    
    // Actions
    loadUserTenants,
    switchTenant,
    createTenant,
    clearStore,
    initStore
  }
})

